"use client";

import { useCallback } from "react";
import { useSeqLog, LogLevel } from "@/hooks/telemetry";
import { useSessionDataContext } from "../data/SessionDataProvider";
import { useSessionEntityLookup } from "../data/hooks";
import type { SessionDataContextValue } from "../data/context";
import {
  useSessionUIContext,
  type AfterCollabReferenceNavigateHandler,
} from "./session-ui-context";
import {
  navigateCollabReferenceTarget,
  navigateFileReferenceTarget,
  navigateProductReferenceTarget,
  navigateVersionReferenceTarget,
} from "./reference-navigation-actions";
import {
  resolveCollabReferenceTarget,
  resolveFileReferenceTarget,
  resolveProductReferenceTarget,
  resolveVersionReferenceTarget,
} from "./reference-navigation-resolvers";
import {
  type ReferenceNavigationApi,
  type ReferenceNavigationHandler,
} from "./reference-navigation-types";

export {
  NavigationOutcome,
  type ReferenceNavigationApi,
  type ReferenceNavigationHandler,
} from "./reference-navigation-types";

const REF_CLICK_DEBUG_FLOW = "ref-click-debug";
const SEQ_LOG_COMPONENT_NAME = "useReferenceNavigation";

type SessionActions = SessionDataContextValue["actions"];
type ReferenceNavigateCallback = () => void;

interface ReferenceNavigateLifecycle {
  callAfterCollabNavigate: AfterCollabReferenceNavigateHandler;
  callBeforeNavigate: ReferenceNavigateCallback;
}

interface ProductNavigationHookInput {
  actions: SessionActions;
  callBeforeNavigate: ReferenceNavigateCallback;
  focusedProductId: string | null;
  products: SessionDataContextValue["products"];
  resolveShortId: ReturnType<typeof useSessionEntityLookup>["resolveShortId"];
  seqLog: ReturnType<typeof useSeqLog>;
}

interface VersionNavigationHookInput extends ProductNavigationHookInput {
  selectedVersionId: string | null;
  versionPreviews: SessionDataContextValue["versionPreviews"];
  versions: SessionDataContextValue["versions"];
}

interface FileNavigationHookInput {
  actions: SessionActions;
  attachedFiles: SessionDataContextValue["attachedFiles"];
  callBeforeNavigate: ReferenceNavigateCallback;
  chatMessages: SessionDataContextValue["chatMessages"];
  collabPreviews: SessionDataContextValue["collabPreviews"];
  collabs: SessionDataContextValue["collabs"];
  focusedProductId: string | null;
  openFilePreview: ReturnType<typeof useSessionUIContext>["openFilePreview"];
  products: SessionDataContextValue["products"];
  resolveShortId: ReturnType<typeof useSessionEntityLookup>["resolveShortId"];
  versionPreviews: SessionDataContextValue["versionPreviews"];
  versions: SessionDataContextValue["versions"];
}

interface CollabNavigationHookInput {
  actions: SessionActions;
  callAfterCollabNavigate: AfterCollabReferenceNavigateHandler;
  callBeforeNavigate: ReferenceNavigateCallback;
  chat: SessionDataContextValue["chat"];
  chatMessages: SessionDataContextValue["chatMessages"];
  collabPreviews: SessionDataContextValue["collabPreviews"];
  collabs: SessionDataContextValue["collabs"];
  products: SessionDataContextValue["products"];
  resolveShortId: ReturnType<typeof useSessionEntityLookup>["resolveShortId"];
  seqLog: ReturnType<typeof useSeqLog>;
}

export function useReferenceNavigation(): ReferenceNavigationApi {
  const context = useSessionDataContext();
  const ui = useSessionUIContext();
  const { resolveShortId } = useSessionEntityLookup();
  const seqLog = useSeqLog(SEQ_LOG_COMPONENT_NAME, {
    flow: REF_CLICK_DEBUG_FLOW,
  });

  const actions = context.actions;
  const attachedFiles = context.attachedFiles;
  const chat = context.chat;
  const chatMessages = context.chatMessages;
  const collabPreviews = context.collabPreviews;
  const collabs = context.collabs;
  const focusedProductId = context.navigation.focusedProductId;
  const products = context.products;
  const selectedVersionId = context.navigation.selectedVersionId;
  const versionPreviews = context.versionPreviews;
  const versions = context.versions;
  const openFilePreview = ui.openFilePreview;
  const { callAfterCollabNavigate, callBeforeNavigate } =
    useReferenceNavigateLifecycle({ seqLog, ui });

  const navigateToProductReference = useProductReferenceNavigation({
    actions,
    callBeforeNavigate,
    focusedProductId,
    products,
    resolveShortId,
    seqLog,
  });
  const navigateToVersionReference = useVersionReferenceNavigation({
    actions,
    callBeforeNavigate,
    focusedProductId,
    products,
    resolveShortId,
    selectedVersionId,
    seqLog,
    versionPreviews,
    versions,
  });
  const navigateToFileReference = useFileReferenceNavigation({
    actions,
    attachedFiles,
    callBeforeNavigate,
    chatMessages,
    collabPreviews,
    collabs,
    focusedProductId,
    openFilePreview,
    products,
    resolveShortId,
    versionPreviews,
    versions,
  });
  const navigateToCollabReference = useCollabReferenceNavigation({
    actions,
    callAfterCollabNavigate,
    callBeforeNavigate,
    chat,
    chatMessages,
    collabPreviews,
    collabs,
    products,
    resolveShortId,
    seqLog,
  });

  return {
    navigateToProductReference,
    navigateToVersionReference,
    navigateToFileReference,
    navigateToCollabReference,
    setBeforeNavigate: ui.setBeforeNavigate,
    setAfterCollabNavigate: ui.setAfterCollabNavigate,
    setOpenFilePreview: ui.setOpenFilePreview,
  };
}

function useReferenceNavigateLifecycle(input: {
  seqLog: ReturnType<typeof useSeqLog>;
  ui: ReturnType<typeof useSessionUIContext>;
}): ReferenceNavigateLifecycle {
  const seqLog = input.seqLog;
  const beforeReferenceNavigateRef = input.ui.beforeReferenceNavigateRef;
  const afterCollabReferenceNavigateRef =
    input.ui.afterCollabReferenceNavigateRef;

  const callBeforeNavigate = useCallback((): void => {
    const hasHandler = beforeReferenceNavigateRef.current != null;
    seqLog("10 callBeforeNavigate invoked", { hasHandler }, LogLevel.Info);
    beforeReferenceNavigateRef.current?.();
    seqLog("10a callBeforeNavigate returned", { hasHandler }, LogLevel.Info);
  }, [beforeReferenceNavigateRef, seqLog]);

  const callAfterCollabNavigate =
    useCallback<AfterCollabReferenceNavigateHandler>(
      (navigateInput): void => {
        const hasHandler = afterCollabReferenceNavigateRef.current != null;
        seqLog(
          "30 callAfterCollabNavigate invoked",
          { hasHandler },
          LogLevel.Info,
        );
        afterCollabReferenceNavigateRef.current?.(navigateInput);
        seqLog(
          "30a callAfterCollabNavigate returned",
          { hasHandler },
          LogLevel.Info,
        );
      },
      [afterCollabReferenceNavigateRef, seqLog],
    );

  return { callAfterCollabNavigate, callBeforeNavigate };
}

function useProductReferenceNavigation({
  actions,
  callBeforeNavigate,
  focusedProductId,
  products,
  resolveShortId,
  seqLog,
}: ProductNavigationHookInput): ReferenceNavigationHandler {
  return useCallback(
    (shortId): ReturnType<ReferenceNavigationHandler> => {
      const target = resolveProductReferenceTarget({
        focusedProductId,
        products,
        resolveShortId,
        shortId,
      });
      return navigateProductReferenceTarget({
        actions,
        callBeforeNavigate,
        focusedProductId,
        seqLog,
        shortId,
        target,
      });
    },
    [
      actions,
      callBeforeNavigate,
      focusedProductId,
      products,
      resolveShortId,
      seqLog,
    ],
  );
}

function useVersionReferenceNavigation({
  actions,
  callBeforeNavigate,
  focusedProductId,
  products,
  resolveShortId,
  selectedVersionId,
  seqLog,
  versionPreviews,
  versions,
}: VersionNavigationHookInput): ReferenceNavigationHandler {
  return useCallback(
    (shortId): ReturnType<ReferenceNavigationHandler> => {
      const target = resolveVersionReferenceTarget({
        focusedProductId,
        products,
        resolveShortId,
        selectedVersionId,
        shortId,
        versionPreviews,
        versions,
      });
      return navigateVersionReferenceTarget({
        actions,
        callBeforeNavigate,
        focusedProductId,
        seqLog,
        shortId,
        target,
      });
    },
    [
      actions,
      callBeforeNavigate,
      focusedProductId,
      products,
      resolveShortId,
      selectedVersionId,
      seqLog,
      versionPreviews,
      versions,
    ],
  );
}

function useFileReferenceNavigation({
  actions,
  attachedFiles,
  callBeforeNavigate,
  chatMessages,
  collabPreviews,
  collabs,
  focusedProductId,
  openFilePreview,
  products,
  resolveShortId,
  versionPreviews,
  versions,
}: FileNavigationHookInput): ReferenceNavigationHandler {
  return useCallback(
    (shortId): ReturnType<ReferenceNavigationHandler> => {
      const target = resolveFileReferenceTarget({
        attachedFiles,
        chatMessages,
        collabPreviews,
        collabs,
        products,
        resolveShortId,
        shortId,
        versionPreviews,
        versions,
      });
      return navigateFileReferenceTarget({
        actions,
        callBeforeNavigate,
        focusedProductId,
        openFilePreview,
        target,
      });
    },
    [
      actions,
      attachedFiles,
      callBeforeNavigate,
      chatMessages,
      collabPreviews,
      collabs,
      focusedProductId,
      openFilePreview,
      products,
      resolveShortId,
      versionPreviews,
      versions,
    ],
  );
}

function useCollabReferenceNavigation({
  actions,
  callAfterCollabNavigate,
  callBeforeNavigate,
  chat,
  chatMessages,
  collabPreviews,
  collabs,
  products,
  resolveShortId,
  seqLog,
}: CollabNavigationHookInput): ReferenceNavigationHandler {
  return useCallback(
    (shortId): ReturnType<ReferenceNavigationHandler> => {
      const target = resolveCollabReferenceTarget({
        chatMessages,
        collabPreviews,
        collabs,
        resolveShortId,
        shortId,
      });
      return navigateCollabReferenceTarget({
        actions,
        callAfterCollabNavigate,
        callBeforeNavigate,
        chat,
        products,
        seqLog,
        shortId,
        target,
      });
    },
    [
      actions,
      callAfterCollabNavigate,
      callBeforeNavigate,
      chat,
      chatMessages,
      collabPreviews,
      collabs,
      products,
      resolveShortId,
      seqLog,
    ],
  );
}
